import { __decorate } from "tslib";
import { Group, MathUtils, Vector3 } from 'three';
import { LabelTextConfig } from '../../Config/LabeltextConfig.js';
import { TibiaAnalyzerLTConfig as Config } from '../../Config/TibiaAnalyzerLTConfig.js';
import { CachedMeasure } from '../../Core/CachedMeasure.js';
import { Save, SaveChild } from '../../Core/Save.js';
import { ApexSign, ViewType } from '../../Models/AppModel.js';
import { SelectedApexMech } from '../../States/State.js';
import { Consts } from '../../Utils/Consts.js';
import { LimitUtils } from '../../Utils/LimitUtils.js';
import { Utils } from '../../Utils/Utils.js';
import { VectorUtils } from '../../Utils/VectorUtils.js';
import { DraggablePoint } from '../Primitive/DraggablePoint.js';
import { LabelAngle } from '../Primitive/Label/LabelAngle.js';
import { LabelText } from '../Primitive/Label/LabelText.js';
import { LimitLine } from '../Primitive/LimitLine.js';
import { LinePointToPoint2 } from '../Primitive/LinePointToPoint2.js';
import { Point } from '../Primitive/Point.js';
import { Hexagon } from '../Primitive/Hexagon.js';
export class MechanicalAxisTibiaLT extends Group {
    get angleForTemplating() {
        return VectorUtils.linesAngleFromOrigin(this.FP.position.clone().sub(this.MA.position), Consts.verDir);
    }
    get PPTAStandard() {
        return this._PPTAStandard;
    }
    set PPTAStandard(value) {
        this._PPTAStandard = value;
        this.update();
    }
    set PPTAStandardDegrees(value) {
        this._PPTAStandard = MathUtils.degToRad(value);
        this.update();
    }
    get ADTAStandard() {
        return this._ADTAStandard;
    }
    set ADTAStandard(value) {
        this._ADTAStandard = value;
        this.update();
    }
    set ADTAStandardDegrees(value) {
        this._ADTAStandard = MathUtils.degToRad(value);
        this.update();
    }
    get apexWithSign() {
        return this.apexAngle.value * this.apexAngleSign.value * (this._isLeft ? 1 : -1);
    }
    get proximalDirToCalculateApex() {
        return this.AP.position.clone().sub(this.PP.position).applyAxisAngle(this._axisForRotation, this._PPTAStandard);
    }
    get distalDirToCalculateApex() {
        return this.AA.position.clone().sub(this.PA.position).applyAxisAngle(this._axisForRotation, this._ADTAStandard);
    }
    get apexBisector() {
        return VectorUtils.getBisector(this.FP.position.clone().sub(this.apex.position), this.MA.position.clone().sub(this.apex.position));
    }
    get mechAxisUpperPoint() {
        return this.FP.position;
    }
    get mechAxisLowerPoint() {
        return this.MA.position;
    }
    constructor(isLeft, isEOC) {
        super();
        this.pointsToRegister = 4;
        this._limitLines = [];
        this._enableLimits = false; // refresh page to make it works (to improve performance when don't use it)
        this.onAfterRestore = () => {
            this.update();
        };
        this.name = 'TibiaAnalyzer';
        this._isLeft = isLeft;
        this._axisForRotation = isLeft ? Consts.planeNormal.clone() : Consts.planeNormal.clone().multiplyScalar(-1);
        this._isEOC = isEOC;
        if (this._enableLimits) {
            this._limitLines[0] = new LimitLine(ViewType.LT, 0xffff00);
            this._limitLines[1] = new LimitLine(ViewType.LT, 0x00ffff);
            this._limitLines[2] = new LimitLine(ViewType.LT, 0xff00ff);
            this._limitLines[3] = new LimitLine(ViewType.LT, 0xffffff);
            this._limitLines[4] = new LimitLine(ViewType.LT, 0xaaaa00);
            this._limitLines[5] = new LimitLine(ViewType.LT, 0x00aaaa);
            this._limitLines[6] = new LimitLine(ViewType.LT, 0xaa00aa);
            this._limitLines[7] = new LimitLine(ViewType.LT, 0xaaaaaa);
        }
        this.add(this.AP = new DraggablePoint('AP', ViewType.LT, Config.AP_color), this.PP = new DraggablePoint('PP', ViewType.LT, Config.PP_color), this.AA = new DraggablePoint('AA', ViewType.LT, Config.AA_color), this.PA = new DraggablePoint('PA', ViewType.LT, Config.PA_color), this.FP = new Point('FP', Config.FP_color), this.MA = new Point('MA', Config.MA_color), this.apex = new Hexagon(ViewType.AP, Config.apex_color), this.anatomicalAxis = new LinePointToPoint2('anatomicalAxis', ViewType.LT, Config.anatomicalAxis_color, this.FP, this.MA), this.AP_PP_Line = new LinePointToPoint2('AP_PP_Line', ViewType.LT, Config.AP_PP_Line_color, this.AP, this.PP), this.AA_PA_Line = new LinePointToPoint2('AA_PA_Line', ViewType.LT, Config.AA_PA_Line_color, this.AA, this.PA), this.proximalMechanicalAxis = new LinePointToPoint2('proximalMechanicalAxis', ViewType.LT, Config.proximalMechanicalAxis_color, this.FP, this.apex, 2, 1, 1.5), this.distalMechanicalAxis = new LinePointToPoint2('distalMechanicalAxis', ViewType.LT, Config.distalMechanicalAxis_color, this.MA, this.apex, 2, 1, 1.5), this.PPTALabel = new LabelAngle({
            name: 'PPTALabel',
            arcA: this.PP,
            arcB: this.MA,
            arcCenter: this.FP,
            viewType: ViewType.LT,
            labelAnchor: new Vector3(isLeft ? -7 : 7, -5),
            labelParams: { backgroundColor: Config.PPTALabel_backgroundColor, fontColor: Config.PPTALabel_fontColor },
            labelErrorParams: { backgroundColor: 'rgba(255, 0, 0, 0.6)', fontColor: '#ffffff' },
            minAngle: Consts.paleyAngles.LT.PPTA.min,
            maxAngle: Consts.paleyAngles.LT.PPTA.max,
            textCallback: (angle) => `PPTA ${angle}°`
        }), this.ADTALabel = new LabelAngle({
            name: 'ADTALabel',
            arcA: this.AA,
            arcB: this.FP,
            arcCenter: this.MA,
            viewType: ViewType.LT,
            labelAnchor: new Vector3(isLeft ? 7 : -7, 5),
            labelParams: { backgroundColor: Config.ADTALabel_backgroundColor, fontColor: Config.ADTALabel_fontColor },
            labelErrorParams: { backgroundColor: 'rgba(255, 0, 0, 0.6)', fontColor: '#ffffff' },
            minAngle: Consts.paleyAngles.LT.ADTA.min,
            maxAngle: Consts.paleyAngles.LT.ADTA.max,
            textCallback: (angle) => `ADTA ${angle}°`
        }), this.apexLabel = new LabelText({
            name: 'APEX',
            color: LabelTextConfig.color,
            viewType: ViewType.LT,
            objToAnchor: this.apex,
            anchor: new Vector3(isLeft ? 7 : -7, 5),
            labelParams: { backgroundColor: Config.apexLabel_backgroundColor, fontColor: Config.apexLabel_fontColor, text: 'APEX' }
        }), ...this._limitLines);
        this.FP.visible = this.MA.visible = false;
        this._PPTAStandard = MathUtils.degToRad(Consts.paleyAngles.LT.PPTA.standard);
        this._ADTAStandard = MathUtils.degToRad(Consts.paleyAngles.LT.ADTA.standard);
        this.bindEvents();
        this.PPTA = new CachedMeasure(() => {
            return this.MA.position.clone().sub(this.FP.position).angleTo(this.PP.position.clone().sub(this.AP.position));
        }, this.MA, this.FP);
        this.ADTA = new CachedMeasure(() => {
            return this.FP.position.clone().sub(this.MA.position).angleTo(this.AA.position.clone().sub(this.PA.position));
        }, this.MA, this.FP);
        this.apexAngleToFix = new CachedMeasure(() => {
            return this.apex.visible ? this.MA.position.clone().sub(this.apex.position).angleTo(this.apex.position.clone().sub(this.FP.position)) : 0;
        }, this.apex);
        this.apexAngle = new CachedMeasure(() => {
            return this.apexAngleToFix.value > Math.PI / 2 ? Math.PI - this.apexAngleToFix.value : this.apexAngleToFix.value;
        }, this.apex);
        this.apexAngleSign = new CachedMeasure(() => {
            if (this.apex.visible) {
                const perpDir = this.apex.position.clone().sub(this.FP.position).cross(this._axisForRotation);
                return Math.sign(this.apex.position.clone().sub(this.MA.position).dot(perpDir)) * (this.apexAngleToFix.value > Math.PI / 2 ? -1 : 1);
            }
            return ApexSign.valgus;
        }, this.apex);
    }
    bindEvents() {
        this.bindEvent('onAfterDragMove', () => {
            this.update();
        });
        this.bindEvent('onDragEnd', () => {
            this.clearLimits();
        });
        this.bindDragLimit(this.AP, this.PP.position, this.AA.position, this.PA.position, true);
        this.bindDragLimit(this.PP, this.AP.position, this.PA.position, this.AA.position, true);
        this.bindDragLimit(this.AA, this.PA.position, this.AP.position, this.PP.position, false);
        this.bindDragLimit(this.PA, this.AA.position, this.PP.position, this.AP.position, false);
    }
    bindDragLimit(point, nearConsecutive, farConsecutive, diagonal, femurLimit) {
        point.bindEvent('onDragMove', (args) => {
            for (let i = 0; i < 2; i++) { //2 cycles removes projection multi limit errors
                LimitUtils.applyLimitAndProjectVector(nearConsecutive, diagonal, args.position, farConsecutive, point.scale.x, this._limitLines[i * 3 + 0]);
                LimitUtils.applyLimitAndProjectVector(diagonal, farConsecutive, args.position, nearConsecutive, point.scale.x, this._limitLines[i * 3 + 1]);
                LimitUtils.applyLimitAndProjectVector(nearConsecutive, farConsecutive, args.position, nearConsecutive.clone().add(nearConsecutive.clone().sub(diagonal)), point.scale.x, this._limitLines[i * 3 + 2]);
                femurLimit && this._AE && LimitUtils.applyLimitAndProjectVector(this._AE, this._PE, args.position, this.PA.position, point.scale.x, this._limitLines[i * 4 + 3]);
            }
        });
    }
    initPositions(positions, fixPosition) {
        fixPosition && this.fixPositionsOrder(positions);
        this.AP.position.copy(positions[0].setZ(0));
        this.PP.position.copy(positions[1].setZ(0));
        this.AA.position.copy(positions[2].setZ(0));
        this.PA.position.copy(positions[3].setZ(0));
        this.update();
    }
    fixPositionsOrder(positions) {
        const middlePointA = positions[0].clone().lerp(positions[1], 0.5);
        const middlePointB = positions[2].clone().lerp(positions[3], 0.5);
        const perpDir = middlePointA.clone().sub(middlePointB).cross(Consts.planeNormal);
        const sign = this._isLeft ? -1 : 1;
        if (Math.sign(positions[0].clone().sub(middlePointA).dot(perpDir)) === sign) {
            Utils.swapItemInArray(0, 1, positions);
        }
        if (Math.sign(positions[2].clone().sub(middlePointA).dot(perpDir)) === sign) {
            Utils.swapItemInArray(2, 3, positions);
        }
    }
    update() {
        this.FP.position.copy(this.AP.position.clone().lerp(this.PP.position, 1 / 5));
        this.MA.position.copy(this.AA.position.clone().lerp(this.PA.position, 0.5));
        const PPTAValid = Utils.isRadiantsAngleInDegreesRange(this.PPTA.value, Consts.paleyAngles.LT.PPTA.min, Consts.paleyAngles.LT.PPTA.max);
        const ADTAValid = Utils.isRadiantsAngleInDegreesRange(this.ADTA.value, Consts.paleyAngles.LT.ADTA.min, Consts.paleyAngles.LT.ADTA.max);
        const invalidAngles = !PPTAValid || !ADTAValid;
        const apex = VectorUtils.lines2DIntersection(this.FP.position, this.FP.position.clone().add(this.proximalDirToCalculateApex), this.MA.position, this.MA.position.clone().add(this.distalDirToCalculateApex));
        apex && this.apex.position.copy(apex);
        this.setApexVisibility(invalidAngles);
        this.dispatchEvent({ type: 'updated' });
    }
    setApexVisibility(visible) {
        if (this._isEOC && (this.selectedApex == SelectedApexMech.femurDistal || this.selectedApex == SelectedApexMech.femurProximal)) {
            visible = false;
        }
        if (this.apex.visible !== visible) {
            this.apex.visible = visible;
            this.apexLabel.visible = visible;
            this.proximalMechanicalAxis.visible = visible;
            this.distalMechanicalAxis.visible = visible;
        }
    }
    clearLimits() {
        for (const limitLine of this._limitLines) {
            limitLine.visible = false;
        }
    }
    setFemurLimits(AE, PE) {
        this._AE = AE;
        this._PE = PE;
    }
}
__decorate([
    Save("position")
], MechanicalAxisTibiaLT.prototype, "AP", void 0);
__decorate([
    Save("position")
], MechanicalAxisTibiaLT.prototype, "PP", void 0);
__decorate([
    Save("position")
], MechanicalAxisTibiaLT.prototype, "AA", void 0);
__decorate([
    Save("position")
], MechanicalAxisTibiaLT.prototype, "PA", void 0);
__decorate([
    SaveChild()
], MechanicalAxisTibiaLT.prototype, "PPTALabel", void 0);
__decorate([
    SaveChild()
], MechanicalAxisTibiaLT.prototype, "ADTALabel", void 0);
__decorate([
    SaveChild()
], MechanicalAxisTibiaLT.prototype, "apexLabel", void 0);
__decorate([
    Save()
], MechanicalAxisTibiaLT.prototype, "selectedApex", void 0);
__decorate([
    Save()
], MechanicalAxisTibiaLT.prototype, "_PPTAStandard", void 0);
__decorate([
    Save()
], MechanicalAxisTibiaLT.prototype, "_ADTAStandard", void 0);
//# sourceMappingURL=TibiaAnalyzerLT.js.map